import { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Card, CardHeader, CardTitle } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { Breadcrumb } from '@/components/ui/Breadcrumb'
import { useCreateClient } from '@/hooks/useCreateClient'
import { useAdmins } from '@/hooks/useAdmins'
import { clientSchema, type ClientFormData } from '@/types/client'
import { ArrowLeft } from 'lucide-react'

export default function NewClientPage() {
  const navigate = useNavigate()
  const createClient = useCreateClient()
  const { data: admins } = useAdmins()
  const [sendEmail, setSendEmail] = useState(true)
  const [selectedAdminId, setSelectedAdminId] = useState<string>('')

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    watch,
    setValue,
  } = useForm<ClientFormData>({
    resolver: zodResolver(clientSchema),
    defaultValues: {
      type_client: 'particulier',
      prenom: '',
      nom: '',
      email: '',
      phone: '',
      adresse: '',
      company: '',
      siret: '',
      tva_intracommunautaire: '',
      adresse_facturation: '',
    },
  })

  const typeClient = watch('type_client')

  const onSubmit = async (data: ClientFormData) => {
    try {
      const client = await createClient.mutateAsync({
        ...data,
        sendWelcomeEmail: sendEmail,
        assignedAdminId: selectedAdminId || undefined,
      })
      navigate(`/clients/${client.id}`)
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : ''
      if (errorMessage.includes('duplicate')) {
        alert('Cet email est deja utilise')
      } else {
        alert('Erreur lors de la creation')
      }
    }
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Breadcrumb */}
      <Breadcrumb
        items={[
          { label: 'Clients', href: '/clients' },
          { label: 'Nouveau client' },
        ]}
      />

      {/* Back button */}
      <Link to="/clients">
        <Button
          variant="ghost"
          size="sm"
          leftIcon={<ArrowLeft className="w-4 h-4" />}
        >
          Retour
        </Button>
      </Link>

      <Card padding="lg">
        <CardHeader>
          <CardTitle>Nouveau client</CardTitle>
          <p className="text-sm text-brown-600">
            Creez un nouveau client pour gerer ses projets et factures
          </p>
        </CardHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Client Type */}
          <div className="space-y-3">
            <label className="text-sm font-medium text-brown-700">
              Type de client *
            </label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  {...register('type_client')}
                  value="particulier"
                  className="w-4 h-4 text-mint-600 border-beige-300 focus:ring-mint-500"
                />
                <span className="text-brown-900">Particulier</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  {...register('type_client')}
                  value="professionnel"
                  className="w-4 h-4 text-mint-600 border-beige-300 focus:ring-mint-500"
                />
                <span className="text-brown-900">Professionnel</span>
              </label>
            </div>
          </div>

          {/* Personal Information */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input
              label="Prenom *"
              {...register('prenom')}
              error={errors.prenom?.message}
            />
            <Input
              label="Nom *"
              {...register('nom')}
              error={errors.nom?.message}
            />
          </div>

          <Input
            label="Email *"
            type="email"
            {...register('email')}
            error={errors.email?.message}
          />

          <Input
            label="Telephone"
            type="tel"
            {...register('phone')}
            error={errors.phone?.message}
          />

          <Input
            label="Adresse"
            {...register('adresse')}
            error={errors.adresse?.message}
          />

          {/* Professional Fields */}
          {typeClient === 'professionnel' && (
            <>
              <Input
                label="Nom de l'entreprise *"
                {...register('company')}
                error={errors.company?.message}
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Input
                  label="SIRET"
                  {...register('siret')}
                  placeholder="14 chiffres"
                  error={errors.siret?.message}
                />
                <Input
                  label="TVA Intracommunautaire"
                  {...register('tva_intracommunautaire')}
                  error={errors.tva_intracommunautaire?.message}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-brown-700 mb-1">
                  Adresse de facturation
                </label>
                <textarea
                  {...register('adresse_facturation')}
                  className="block w-full px-4 py-2 bg-white border border-beige-300 rounded-lg text-brown-900 placeholder-brown-400 focus:outline-none focus:ring-2 focus:ring-mint-500 focus:border-transparent transition-colors duration-200 min-h-[80px]"
                />
              </div>
            </>
          )}

          {/* Admin Assignment (STORY-008) */}
          <Select
            label="Attribuer a un administrateur"
            value={selectedAdminId}
            onChange={(e) => setSelectedAdminId(e.target.value)}
          >
            <option value="">Moi-meme (par defaut)</option>
            {admins?.map((admin) => (
              <option key={admin.id} value={admin.id}>
                {admin.full_name} ({admin.clients_count} clients)
              </option>
            ))}
          </Select>

          {/* Options */}
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="sendEmail"
              checked={sendEmail}
              onChange={(e) => setSendEmail(e.target.checked)}
              className="w-4 h-4 text-mint-600 border-beige-300 rounded focus:ring-mint-500"
            />
            <label htmlFor="sendEmail" className="text-sm text-brown-700">
              Envoyer un email de bienvenue au client
            </label>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t border-beige-200">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/clients')}
            >
              Annuler
            </Button>
            <Button type="submit" isLoading={isSubmitting || createClient.isPending}>
              Creer le client
            </Button>
          </div>
        </form>
      </Card>
    </div>
  )
}
